import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  AwBadgeComponent,
  AwTableComponent,
  AwDividerComponent,
  AwActionBarComponent,
  ActionBarRight,
  AwSpinnerComponent,
  AwDialogComponent,
  DialogOptions,
  AwToastComponent,
  ToastVariants,
  TableRow,
} from '@assetworks-llc/aw-component-lib';
import {
  TableCellInput,
  TableCellTypes,
} from '@assetworks-llc/aw-component-lib';
import { AwDateFormComponent } from '../../../../shared/components/aw-date-form/aw-date-form.component';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import { ThemeService } from '../../../../core/services/theme.service';
import {
  catchError,
  finalize,
  forkJoin,
  mergeMap,
  of,
} from 'rxjs';
import { EmployeeAuthenticationService } from '../../../../core/services/employee-authentication.service';
import { SecretService } from '../../../../core/services/secret.service';
import {
  Organization,
  UserService,
} from '../../../../core/services/user.service';

@Component({
  selector: 'app-parent-integration-management',
  imports: [
    AwSpinnerComponent,
    AwToastComponent,
    AwDialogComponent,
    AwTableComponent,
    AwDividerComponent,
    CommonModule,
    AwDateFormComponent,
    AwActionBarComponent,
  ],
  templateUrl: './parent-integration-management.component.html',
  styleUrls: [
    './parent-integration-management.component.scss',
    '../../../../../styles/management.scss',
  ],
  standalone: true,
})
export class ParentIntegrationManagementComponent implements OnInit {
  constructor(
    private router: Router,
    private parentIntegrationService: ParentIntegrationService,
    private employeeAuthenticationService: EmployeeAuthenticationService,
    private secretService: SecretService,
    private themeService: ThemeService,
    private userService: UserService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  container_theme = computed(() => this.themeService.themeSignal());

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];
  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  parentIntegrationToDelete: TableRow | null = null;
  openDeleteDialog: boolean = false;
  userPermissions: Record<string, boolean> = {};

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Add Parent Integration',
        buttonType: 'outlined',
        action: () =>
          this.router.navigate(['/parent-integration-management/add-parent-integration']),
      },
    },
  ]);

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Parent Integration`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();

    if (this.userPermissions['canDelete']) {
      this.columnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'deleteButton',
        label: 'Remove',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: TableRow) => {
          this.openDeleteDialog = true;
          this.parentIntegrationToDelete = data;
        },
      });
    }

    this.parentIntegrationService
      .getAllParentIntegrations()
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed To Load Parent Integrations', 'error');
          return of([]);
        })
      )
      .subscribe((parentIntegrations) => {
        const parentIntegrationData: any[] =
          parentIntegrations?.map((parentIntegration) => ({
            parentIntegrationInfo: {
              name: parentIntegration.name,
              id: parentIntegration.organizationId,
              integrationIds: parentIntegration.integrationIds,
              detailsPageRoute: `/parent-integration-management/parent-integration-details/${parentIntegration.organizationId}`,
            },
            statusBadge: {
              enabled: parentIntegration.enabled,
              color: parentIntegration.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: parentIntegration.enabled ? 'Active' : 'Disabled',
            },
            num_integrations: parentIntegration.integrationIds?.length ?? 0,
            modified_date: parentIntegration.modifiedOn
              ? new Date(parentIntegration.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_created: parentIntegration.createdOn
              ? new Date(parentIntegration.createdOn).toLocaleDateString()
              : 'N/A',
          })) ?? [];
        this.tableData.set(parentIntegrationData);
        this.tableDataCopy = parentIntegrationData;
      });
  }

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'parent_integration_name',
      combineFields: [
        'parentIntegrationInfo.name',
        'parentIntegrationInfo.detailsPageRoute',
        'parentIntegrationInfo.id',
      ],
      label: 'Parent Integration Name',
      combineTemplate: (values: any[]) => ({
        template: this.CustomTemplate(values),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['statusBadge.color', 'statusBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'num_integrations',
      label: 'Number of Integrations',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'date_created',
      label: 'Created On',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];

  CustomTemplate = (values: any) => `
  <div class="custom-template">
    <a href="${values[1]}">${values[0]}</a>
  <p>${values[2]}</p>
  </div>`;

  onDateSelected(date: Date | null) {
    if (date === null) {
      this.awTableComponent.applyFilter('');
      return;
    }
    const filteredDate: string = date!.toLocaleDateString();
    this.awTableComponent.applyFilter(filteredDate);
  }

  dialogFormDeleteHandler(formValues: any) {
    if (this.parentIntegrationToDelete) {
      const parentIntegrationId: string =
        this.parentIntegrationToDelete['parentIntegrationInfo'].id;

      this.isLoading = true;

      this.secretService
        .listAllSecrets({ organizationId: parentIntegrationId })
        .pipe(
          mergeMap((secrets) => {
            if (!secrets || secrets.length === 0) {
              return of([]);
            }
            return forkJoin(
              secrets.map((secret) =>
                this.secretService
                  .deleteSecret({
                    organizationId: parentIntegrationId,
                    secretName: secret.name || '',
                  })
                  .pipe(catchError(() => of(false)))
              )
            );
          }),
          catchError(() => of([])),
          mergeMap(() =>
            this.employeeAuthenticationService.deleteAuthenticationConfig({ 
              organizationId: parentIntegrationId 
            })
          ),
          catchError(() => of(false)),
          mergeMap(() =>
            this.parentIntegrationService.deleteParentIntegration({
              organizationId: parentIntegrationId,
            })
          ),
          finalize(() => {
            this.isLoading = false;
            this.openDeleteDialog = false;
            this.parentIntegrationToDelete = null;
          })
        )
        .subscribe({
          next: (deleted) => {
            const filtered = this.tableDataCopy.filter(
              (row) => row['parentIntegrationInfo'].id !== parentIntegrationId
            );
            this.tableData.set(filtered);
            this.tableDataCopy = [...filtered];
            this.buildToastMessage(
              'Successfully deleted parent integration',
              'success'
            );
          },
          error: () => {
            this.buildToastMessage('Failed to delete parent integration', 'error');
          },
        });
    }
  }

  dialogCloseHandler() {
    this.openDeleteDialog = false;
  }
}
